// Authenticated venue WebSocket, opened directly from the page; auth headers are injected via the extension.

import { callBridge } from './bridge';
import { logWarn } from '../core/log';

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed';

export interface VenueWsOptions {
  wsUrl: string;
  /** Tickers to (re)subscribe to on every successful open. */
  currentTickers: () => string[];
  onMessage: (msg: any) => void;
  onStatus: (status: WsStatus, detail?: string) => void;
  /** Whose key signs the handshake; the extension injects that venue's headers. Default Kalshi. */
  venue?: string;
  /** Handshake path to sign, when it is not the venue's default socket. */
  wsPath?: string;
  /** Frames sent right after open, before market subscriptions. Default: Kalshi's fill + user_orders channels. */
  onOpenMessages?: (nextId: () => number) => unknown[];
  /** The frame that subscribes market data for a set of tickers. Default: Kalshi's orderbook_delta + trade. */
  subscribeMessage?: (tickers: string[], nextId: () => number) => unknown | null;
  /** A frame the venue always answers, sent while open to prove the socket still delivers. Default: Kalshi's list_subscriptions; null disables. */
  probeMessage?: ((nextId: () => number) => unknown) | null;
}

/** How often the probe goes out, and how long total silence may last before the socket is treated as dead. */
const PROBE_MS = 10_000;
const SILENT_MS = 20_000;

/** Kept under the old name so the engine's imports and the stub's URL sniffing are untouched. */
export type KalshiWsOptions = VenueWsOptions;

export class VenueWs {
  private ws: WebSocket | null = null;
  private subId = 1;
  private backoffMs = 1_000;
  private stopped = false;
  private armLock: Promise<unknown> = Promise.resolve();
  private reconnectTimer: number | null = null;
  private connecting = false;
  private lastRxAt = 0;
  private probeTimer: number | null = null;

  constructor(private opts: VenueWsOptions) {}

  private get venue(): string { return this.opts.venue ?? 'kalshi'; }

  // Read fresh on every connect: the page mounts before the extension says which host to talk to.
  get wsUrl(): string { return this.opts.wsUrl; }

  set wsUrl(url: string) { this.opts.wsUrl = url; }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  start(): void {
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.stopProbe();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try {
      this.ws?.close();
    } catch {
      /* already gone */
    }
    this.ws = null;
    void callBridge({ type: 'DISARM_WS', venue: this.venue, path: this.opts.wsPath }, 4_000);
    this.opts.onStatus('closed', 'stopped');
  }

  /** Sign, arm the DNR rule, then open. Serialised against other arms. */
  private async connect(): Promise<void> {
    if (this.stopped || this.connecting) return;
    this.connecting = true;
    this.opts.onStatus('connecting');
    try {
      await this.arm();
      const ws = new WebSocket(this.opts.wsUrl);
      this.ws = ws;

      ws.onopen = () => {
        this.backoffMs = 1_000;
        // Disarmed immediately: while armed the rule affects any WebSocket to the venue, including a user tab.
        void callBridge({ type: 'DISARM_WS', venue: this.venue, path: this.opts.wsPath }, 4_000);
        this.lastRxAt = Date.now();
        this.startProbe();
        this.opts.onStatus('open');
        for (const m of this.openMessages()) this.send(m);
        const tickers = this.opts.currentTickers();
        if (tickers.length) this.subscribeTickers(tickers);
      };

      ws.onmessage = (ev) => {
        this.lastRxAt = Date.now();
        let parsed: any;
        try {
          parsed = JSON.parse(ev.data as string);
        } catch {
          return;
        }
        try {
          this.opts.onMessage(parsed);
        } catch (e) {
          console.error('[ws] handler error', e);
        }
      };

      ws.onerror = () => {
        // The close event carries the useful information; onerror is opaque.
        this.opts.onStatus('connecting', 'handshake error — check DevTools → Network → WS (403 = origin not stripped, 401 = signature rejected)');
      };

      ws.onclose = (ev) => {
        if (this.ws !== ws) return; // already dropped by restart()
        this.ws = null;
        this.connecting = false;
        this.stopProbe();
        if (this.stopped) return;
        this.opts.onStatus('closed', `code ${ev.code}${ev.reason ? ` ${ev.reason}` : ''}`);
        this.scheduleReconnect();
      };
    } catch (e) {
      this.connecting = false;
      this.opts.onStatus('closed', String((e as Error).message || e));
      this.scheduleReconnect();
      return;
    }
    // `connecting` stays true until open or close resolves it.
    if (this.ws) {
      const ws = this.ws;
      const clear = () => { this.connecting = false; };
      ws.addEventListener('open', clear, { once: true });
    }
  }

  /** Asks the extension to sign the handshake and write the header rule; the page never sees the signature. */
  private async arm(): Promise<void> {
    const run = this.armLock.then(async () => {
      const resp = await callBridge({ type: 'ARM_WS', venue: this.venue, path: this.opts.wsPath }, 6_000);
      if (!resp.ok) throw new Error(`could not arm the extension rule: ${resp.error}`);
    });
    this.armLock = run.catch(() => undefined);
    return run;
  }

  /** Drops an open socket that has stopped delivering and reconnects; status goes to closed first, so books read stale. */
  restart(reason: string): void {
    const ws = this.ws;
    if (!ws || this.stopped) return;
    logWarn(`${this.venue}-ws`, `reconnecting: ${reason}`);
    this.ws = null;
    this.connecting = false;
    this.stopProbe();
    try {
      ws.close(4000, 'silent');
    } catch {
      /* already gone */
    }
    this.opts.onStatus('closed', reason);
    this.scheduleReconnect();
  }

  private startProbe(): void {
    this.stopProbe();
    const probe = this.opts.probeMessage === undefined
      ? (nextId: () => number) => ({ id: nextId(), cmd: 'list_subscriptions' })
      : this.opts.probeMessage;
    if (!probe) return;
    this.probeTimer = window.setInterval(() => {
      if (Date.now() - this.lastRxAt > SILENT_MS) {
        this.restart(`nothing received for ${Math.round((Date.now() - this.lastRxAt) / 1000)}s`);
        return;
      }
      this.send(probe(this.nextId));
    }, PROBE_MS);
  }

  private stopProbe(): void {
    if (this.probeTimer !== null) {
      clearInterval(this.probeTimer);
      this.probeTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = this.backoffMs;
    // Capped low since order placement is blocked while the feed is down, so a long backoff is dead time.
    this.backoffMs = Math.min(this.backoffMs * 2, 5_000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  private send(obj: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
  }

  // `use_yes_price: false` is pinned: the book code keys NO-side levels by the NO-leg price.
  subscribeTickers(tickers: string[]): void {
    if (!tickers.length) return;
    const frame = this.subscribeFrame(tickers);
    if (frame) this.send(frame);
  }

  private nextId = (): number => this.subId++;

  private openMessages(): unknown[] {
    if (this.opts.onOpenMessages) return this.opts.onOpenMessages(this.nextId);
    return [{ id: this.nextId(), cmd: 'subscribe', params: { channels: ['fill', 'user_orders'] } }];
  }

  private subscribeFrame(tickers: string[]): unknown {
    if (this.opts.subscribeMessage) return this.opts.subscribeMessage(tickers, this.nextId);
    return {
      id: this.nextId(),
      cmd: 'subscribe',
      params: { channels: ['orderbook_delta', 'trade'], market_tickers: tickers, use_yes_price: false },
    };
  }
}

/** Kalshi's socket: the defaults above, under the name the engine has always used. */
export class KalshiWs extends VenueWs {}
